import React, { useMemo, useState } from 'react';

import { Flex, Form, Input, Modal } from 'antd-v5';
import cn from 'classnames';
import { get, isNil } from 'lodash';
import { MdAddCircle, MdCancel } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Select } from 'scouting-ui/lib';
import variables from 'scouting-ui/lib/variables';

import type {
  CounselorClass,
  CounselorYouth,
  PostCreateCounselorClassRequest,
} from '@appTypes/esb';
import {
  usePostAddScoutsToClassMutation,
  usePostCreateCounselorClassMutation,
  usePostRemoveScoutsFromClassMutation,
  usePutUpdateCounselorClassMutation,
} from '@modules/meritBadgeCounselors/api';
import { useMeritBadgeCounselorProfile } from '@modules/meritBadgeCounselors/hooks';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import { useYouthGroupedByClass } from '../duck/hooks';
import { dashboardSortByNameSel, dashboardYouthsSel } from '../duck/selectors';
import { compareByName } from '../duck/utils';
import styles from './MBCClassModal.module.scss';
import MBImage from './MBImage';
import { MovePerson } from './MovePerson';

interface Props {
  mbcMemberId: string;
  showModal: boolean;
  classId: number | null;
  originalMembers?: CounselorClass['scouts'];
  onClose: VoidFunction;
}

const renderMBOption = (option: {
  data: { image?: string; label?: React.ReactNode };
}) => (
  <Flex align="center" gap={4}>
    <MBImage height={'auto'} width={24} src={option.data.image ?? ''} />
    {option.data.label}
  </Flex>
);

interface MBCClassForm {
  ClassName: string;
  Description: string;
  MBId: number;
  youthIds: number[];
}

enum FormFields {
  ClassName = 'ClassName',
  Description = 'Description',
  MBId = 'MBId',
}

export const MBCClassModal: React.FC<Props> = ({
  mbcMemberId,
  showModal,
  classId,
  originalMembers = [],
  onClose,
}) => {
  const [form] = Form.useForm<MBCClassForm>();
  const { meritBadges, loading: loadingProfile } =
    useMeritBadgeCounselorProfile({
      memberId: mbcMemberId,
      extendedInfo: true,
    });
  const dispatch = useDispatch();

  const { mbClassesDict } = useYouthGroupedByClass();

  const youths = useSelector(dashboardYouthsSel);
  const sortByName = useSelector(dashboardSortByNameSel);

  const [searchStr, setSearchStr] = useState<string>('');
  const [selectedMemberIds, setSelectedMemberIds] = useState(
    () => new Set(originalMembers.map(({ scoutMemberId }) => scoutMemberId)),
  );

  const [createMBCClass, { isLoading: isCreatingClass }] =
    usePostCreateCounselorClassMutation();

  const [updateMBCClass, { isLoading: isUpdatingClass }] =
    usePutUpdateCounselorClassMutation();

  const [addYouthsToMBCClass, { isLoading: isAddingYouthsToClass }] =
    usePostAddScoutsToClassMutation();

  const [removeYouthsFromMBCClass, { isLoading: isRemovingYouthsFromClass }] =
    usePostRemoveScoutsFromClassMutation();

  const loading =
    isCreatingClass ||
    isUpdatingClass ||
    isAddingYouthsToClass ||
    isRemovingYouthsFromClass;

  const commonRules = [
    {
      required: true,
      message: intl.formatMessage({
        id: 'shared.form.error.isRequired',
      }),
    },
  ];

  const mbId = Form.useWatch('MBId', form);

  const { selectedUsers, availableUsers } = useMemo(
    () =>
      youths
        .filter(youth =>
          youth.meritBadgesCounseling.some(mb => mb.meritBadgeId === mbId),
        )
        .sort((a, b) => compareByName(a, b, sortByName))
        .reduce(
          (acc, p) => {
            const { memberId } = p;
            if (!memberId) return acc;
            if (selectedMemberIds.has(memberId)) {
              return {
                ...acc,
                selectedUsers: [...acc.selectedUsers, p],
              };
            }
            return {
              ...acc,
              availableUsers: [...acc.availableUsers, p],
            };
          },
          {
            selectedUsers: [] as CounselorYouth[],
            availableUsers: [] as CounselorYouth[],
          },
        ),
    [mbId, youths, selectedMemberIds, sortByName],
  );

  const bothListEmpty =
    selectedUsers.length === 0 && availableUsers.length === 0;

  const handleAddPerson = (memberId: number) => {
    setSelectedMemberIds(new Set([...selectedMemberIds, memberId]));
  };

  const handleRemovePerson = (memberId: number) => {
    setSelectedMemberIds(
      new Set([...selectedMemberIds].filter(id => id !== memberId)),
    );
  };

  const handleClickSave = async () => {
    try {
      const valuesSubmitted = await form.validateFields();

      if (isNil(classId)) {
        if (selectedMemberIds.size === 0) {
          return toastService.error(
            <FormattedMessage id={'mbcDashboard.mbcClassModal.emptyCreate'} />,
          );
        }

        const payload: PostCreateCounselorClassRequest = {
          className: valuesSubmitted.ClassName,
          classDescription: valuesSubmitted.Description,
          meritBadgeId: valuesSubmitted.MBId,
          counselorMemberId: Number(mbcMemberId),
          scoutMemberIds: [...selectedMemberIds],
        };

        await createMBCClass({ payload });

        toastService.success(
          <FormattedMessage id={'mbcDashboard.mbcClassModal.createClass'} />,
        );
      } else {
        const tags = {
          counselorMemberId: Number(mbcMemberId),
        };
        const update = {
          className: valuesSubmitted.ClassName,
          classDescription: valuesSubmitted.Description,
        };

        await updateMBCClass({ counselorClassId: classId, payload: update });
        const currentMembers = new Set(
          originalMembers.map(scout => scout.scoutMemberId),
        );
        const membersToAdd = {
          scoutMemberIds: [...selectedMemberIds].filter(
            id => !currentMembers.has(id),
          ),
        };
        const membersToRemove = {
          scoutMemberIds: [...currentMembers].filter(
            id => !selectedMemberIds.has(id),
          ),
        };

        if (membersToAdd.scoutMemberIds.length) {
          await addYouthsToMBCClass({
            counselorClassId: classId,
            payload: membersToAdd,
          });
        }
        if (membersToRemove.scoutMemberIds.length) {
          await removeYouthsFromMBCClass({
            counselorClassId: classId,
            payload: membersToRemove,
          });
        }

        toastService.success(
          <FormattedMessage id={'mbcDashboard.mbcClassModal.updateClass'} />,
        );

        dispatch(
          esbApi.util.invalidateTags(tagFactory.MBCClasses.mbcClasses(tags)),
        );
      }
      onClose();
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
      return;
    }
  };

  const options = useMemo(
    () =>
      meritBadges.map(badge => ({
        label: badge.meritBadgeName,
        value: badge.meritBadgeId,
        image: badge.imageUrl100,
      })),
    [meritBadges],
  );

  return (
    <Modal
      width={800}
      open={showModal}
      destroyOnClose
      title={
        <FormattedMessage
          id={
            isNil(classId)
              ? 'mbcDashboard.mbcClassModal.title'
              : 'mbcDashboard.mbcClassModal.editTitle'
          }
        />
      }
      cancelText={<FormattedMessage id="shared.cancel" />}
      okText={<FormattedMessage id="shared.save" />}
      onOk={handleClickSave}
      cancelButtonProps={{
        type: 'default',
        onClick: onClose,
        disabled: loading,
      }}
      okButtonProps={{ disabled: loadingProfile || bothListEmpty, loading }}
      onCancel={onClose}
    >
      <Form
        clearOnDestroy
        form={form}
        initialValues={
          classId
            ? {
                ClassName: mbClassesDict[classId].className,
                Description: mbClassesDict[classId].classDescription,
                MBId: mbClassesDict[classId].scoutbookId,
                youthIds: mbClassesDict[classId].scouts.map(
                  scout => scout.scoutMemberId,
                ),
              }
            : {}
        }
      >
        <div className={styles.inputContainer}>
          <Form.Item name={FormFields.ClassName} rules={commonRules} noStyle>
            <Input
              placeholder={intl.formatMessage({
                id: 'mbcDashboard.mbcClassModal.className',
              })}
              size="small"
            />
          </Form.Item>
        </div>

        <div className={styles.inputContainer}>
          <Form.Item name={FormFields.Description} rules={commonRules} noStyle>
            <Input
              placeholder={intl.formatMessage({
                id: 'mbcDashboard.mbcClassModal.description',
              })}
              size="small"
            />
          </Form.Item>
        </div>

        <div className={styles.inputContainer}>
          <Form.Item name={FormFields.MBId} rules={commonRules} noStyle>
            <Select
              placeholder={intl.formatMessage({
                id: 'mbcDashboard.mbcClassModal.mbSelection',
              })}
              disabled={!!classId}
              optionFilterProp="label"
              showSearch
              options={options}
              optionRender={renderMBOption}
            />
          </Form.Item>
        </div>
        <Flex justify="flex-end">
          <Search
            placeholder={intl.formatMessage({
              id: 'mbcDashboard.actions.search',
            })}
            className={styles.inputContainer}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchStr(e.target.value)
            }
            size="small"
          />
        </Flex>
        <div className={styles.youtLists}>
          <div className={cn(styles.inputContainer, styles.availableList)}>
            <MovePerson
              onSelect={handleAddPerson}
              selectIcon={<MdAddCircle color={variables['color-info']} />}
              persons={availableUsers}
              emptyListTitle={
                !bothListEmpty && (
                  <FormattedMessage
                    id={'mbcDashboard.mbcClassModal.fullTitle'}
                  />
                )
              }
              emptyListDsc={
                !bothListEmpty && (
                  <FormattedMessage
                    id={'mbcDashboard.mbcClassModal.emptySelectDsc'}
                  />
                )
              }
              searchStr={searchStr}
            />
          </div>
          <div className={cn(styles.inputContainer, styles.selectedList)}>
            <MovePerson
              onSelect={handleRemovePerson}
              selectIcon={<MdCancel color={variables['color-text-regular']} />}
              persons={selectedUsers}
              emptyListTitle={
                !bothListEmpty && (
                  <FormattedMessage
                    id={'mbcDashboard.mbcClassModal.emptyTitle'}
                  />
                )
              }
              emptyListDsc={
                !bothListEmpty && (
                  <FormattedMessage
                    id={'mbcDashboard.mbcClassModal.emptyDeselectDsc'}
                  />
                )
              }
              searchStr={searchStr}
            />
          </div>
        </div>
      </Form>
    </Modal>
  );
};
